import { Column, Table } from "@tanstack/react-table";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ModalWrapper from "..";
import { Eye, EyeOff, Menu } from "lucide-react";
import {
  STATUS_INDICATOR_COLUMN,
  tableColumnsAr,
  tableColumnsEn,
} from "../../../constants/common";
import { useTranslation } from "react-i18next";
import { useTableStore } from "../../../store/Table";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface ColumnChooserModalProps {
  table: Table<{
    [key: string]: any;
  }>;
  isOpen: boolean;
  onOpenChange: () => void;
  tableId?: string;
  isHideStatusIndicator?: boolean;
}

interface SortableItemProps {
  column: Column<
    {
      [key: string]: any;
    },
    unknown
  >;
  onClick: (e: any) => void;
}

const ColumnChooserModal = ({
  table,
  isOpen,
  onOpenChange,
  tableId = "",
  isHideStatusIndicator = false,
}: ColumnChooserModalProps) => {
  const {
    columnOrder: storedColumnOrder,
    setColumnOrder: setColumnOrderInStore,
    columnVisibility,
    setColumnVisibility,
  } = useTableStore();

  const [columnOrder, setColumnOrder] = useState<string[]>(
    storedColumnOrder[tableId] || table.getAllColumns().map((col) => col.id)
  );

  useEffect(() => {
    table.setColumnOrder(columnOrder);
  }, [columnOrder, table]);

  // Drag and Drop Sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback(
    (event: any) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = columnOrder.indexOf(active.id);
      const newIndex = columnOrder.indexOf(over.id);
      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);

      setColumnOrder(newOrder);
      table.setColumnOrder(newOrder);
      setColumnOrderInStore(tableId, newOrder);
    },
    [columnOrder, setColumnOrderInStore, table, tableId]
  );

  return (
    <ModalWrapper
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentStyles="md:!max-w-xl"
    >
      <div className="max-h-[70vh] overflow-y-auto w-full">
        <div className="flex flex-col gap-1 w-full px-6 sm:px-[10%]">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={columnOrder}>
              {columnOrder.map((columnId) => {
                if (
                  (isHideStatusIndicator &&
                    (columnId === STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS ||
                      columnId === STATUS_INDICATOR_COLUMN.ADMIN_STATUS)) ||
                  columnId === STATUS_INDICATOR_COLUMN.SELECT
                )
                  return <React.Fragment key={columnId} />;

                const column = table.getColumn(columnId);
                return column ? (
                  <SortableItem
                    key={columnId}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      const newVisibility = {
                        ...columnVisibility[tableId],
                        [columnId]: !columnVisibility[tableId]?.[columnId],
                      };

                      setColumnVisibility(tableId, newVisibility);
                      column.toggleVisibility(!column.getIsVisible());
                    }}
                    column={column}
                  />
                ) : null;
              })}
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </ModalWrapper>
  );
};

export default React.memo(ColumnChooserModal);

const SortableItem = ({ column, onClick }: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: column.id });

  const { i18n } = useTranslation();

  const style = useMemo(
    () => ({
      transform: CSS.Transform.toString(transform),
      transition,
    }),
    [transform, transition]
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="flex items-center justify-between overflow-hidden bg-gray-100 rounded-md cursor-grab"
    >
      <div className="flex gap-2 items-center p-2 flex-1">
        <span className="cursor-grab">
          <Menu className="w-5 h-5 text-gray-500" />
        </span>
        <p className="sm:text-lg">
          {i18n.language === "en"
            ? tableColumnsEn[column.id] || column.id
            : tableColumnsAr[column.id] || column.id}
        </p>
      </div>
      <button
        className=" px-2 py-3"
        onPointerDown={(e) => e.stopPropagation()} // Prevent drag when clicking the button
        onClick={onClick}
      >
        {column.getIsVisible() ? (
          <Eye className="w-5 md:w-6" />
        ) : (
          <EyeOff className="w-5 md:w-6" />
        )}
      </button>
    </div>
  );
};
